import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAreaBreakdown, useIndirectCostRealAreas } from '@/hooks/useSubscriptions';
import type { AreaBreakdownRow, GlobalSubscriptionFilters, IndirectCostRealAreaRow } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

interface AreaSummary {
  area: string;
  total: number;
  ai: number;
  transactions: number;
  users: number;
  vendors: number;
  rows: AreaBreakdownRow[];
}

function buildAreaSummaries(rows: AreaBreakdownRow[], noDeptLabel: string): AreaSummary[] {
  const map = new Map<string, AreaSummary>();
  for (const r of rows) {
    const key = r.NetSuite_Departamentos || noDeptLabel;
    if (!map.has(key)) {
      map.set(key, { area: key, total: 0, ai: 0, transactions: 0, users: 0, vendors: 0, rows: [] });
    }
    const s = map.get(key)!;
    s.total += r.Total_Amount;
    s.ai += r.Is_AI_Tool ? r.Total_Amount : 0;
    s.transactions += r.Transactions;
    s.users = Math.max(s.users, r.Users);
    s.vendors += 1;
    s.rows.push(r);
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

function IndirectRealAreaPanel({ realAreas }: { realAreas: IndirectCostRealAreaRow[] }) {
  const { t } = useTranslation();
  const [expandedRealArea, setExpandedRealArea] = useState<string | null>(null);
  const total = realAreas.reduce((s, a) => s + a.Total_Amount, 0);
  const BAR_COLORS = ['#CEABFF', '#87DBFF', '#FFD447', '#FFA88E', '#1CC9B9', '#F54D25', '#6366f1', '#10b981', '#f97316', '#ec4899'];

  if (realAreas.length === 0) return null;

  return (
    <div className="border-t border-dashed border-brand-green/30 bg-brand-green/3">
      <div className="px-5 py-2.5 flex items-center gap-2">
        <div className="h-1.5 w-1.5 rounded-full bg-brand-green" />
        <p className="text-xs font-semibold text-brand-green uppercase tracking-widest">{t('subscriptions.areadrilldowntab.por_area_real_community_map')}</p>
      </div>
      <div className="divide-y divide-dashed divide-brand-green/20">
        {realAreas.map((a, i) => {
          const pct = total > 0 ? (a.Total_Amount / total) * 100 : 0;
          const barColor = BAR_COLORS[i % BAR_COLORS.length];
          const isExpanded = expandedRealArea === a.realArea;
          return (
            <div key={a.realArea}>
              <button
                className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-muted/30 transition-colors text-left"
                onClick={() => setExpandedRealArea(isExpanded ? null : a.realArea)}
              >
                <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium truncate">{a.realArea}</span>
                    <span className="text-xs font-bold shrink-0" style={{ color: barColor }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="text-xs font-semibold">{fmt(a.Total_Amount)}</p>
                  <p className="text-xs text-muted-foreground">{a.vendors.length} vendor{a.vendors.length !== 1 ? 's' : ''} · {a.Users} usuario{a.Users !== 1 ? 's' : ''}</p>
                </div>
                {isExpanded
                  ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                }
              </button>
              {isExpanded && (
                <div className="bg-muted/10 border-t border-dashed border-brand-green/20">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30">
                      <tr className="text-muted-foreground uppercase tracking-wide">
                        <th className="text-left py-1.5 px-8 font-medium">{t('subscriptions.areadrilldowntab.vendor_tool')}</th>
                        <th className="text-right py-1.5 px-3 font-medium">Monto</th>
                        <th className="text-right py-1.5 px-3 font-medium">Txns</th>
                        <th className="text-center py-1.5 px-3 font-medium">AI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.vendors.map((v) => (
                        <tr key={v.name} className="border-t hover:bg-muted/20">
                          <td className="py-1.5 px-8 font-medium">{v.name}</td>
                          <td className="py-1.5 px-3 text-right font-semibold">{fmt(v.Total_Amount)}</td>
                          <td className="py-1.5 px-3 text-right text-muted-foreground">{v.Transactions}</td>
                          <td className="py-1.5 px-3 text-center">
                            {v.Is_AI_Tool && <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">AI</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AreaDrilldownTab({ globalFilters = {} }: { globalFilters?: GlobalSubscriptionFilters }) {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useAreaBreakdown({
    dateFrom: globalFilters.dateFrom,
    dateTo: globalFilters.dateTo,
    isAITool: globalFilters.isAITool,
    department: globalFilters.department,
    vendor: globalFilters.vendor,
  });
  const { data: indirectRealAreas = [] } = useIndirectCostRealAreas({
    dateFrom: globalFilters.dateFrom,
    dateTo: globalFilters.dateTo,
  });
  const [expandedArea, setExpandedArea] = useState<string | null>(null);
  const [filterAI, setFilterAI] = useState<boolean | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string>('__init__');

  // All unique categories from the data
  const availableCategories = useMemo(() =>
    [...new Set(rows.map((r) => r.Subscription_Category).filter(Boolean))].sort(),
    [rows]
  );

  // Resolve initial category: pick "Indirect Cost" if exists, otherwise first category
  const resolvedCategory = useMemo(() => {
    if (selectedCategory !== '__init__') return selectedCategory;
    if (availableCategories.length === 0) return 'all';
    const indirect = availableCategories.find((c) => c.toLowerCase().includes('indirect'));
    return indirect ?? availableCategories[0];
  }, [selectedCategory, availableCategories]);

  // Apply category filter on top of rows
  const filteredRows = useMemo(() =>
    resolvedCategory === 'all' ? rows : rows.filter((r) => r.Subscription_Category === resolvedCategory),
    [rows, resolvedCategory]
  );

  const areas = useMemo(() => buildAreaSummaries(filteredRows, t('subscriptions.area.no_dept')), [filteredRows, t]);

  const chartData = areas.slice(0, 10).map((a, i) => ({
    area: a.area.length > 20 ? a.area.slice(0, 18) + '…' : a.area,
    fullArea: a.area,
    'Non-AI': a.total - a.ai,
    'AI Tools': a.ai,
    color: COLORS[i % COLORS.length],
  }));

  const totalAll = areas.reduce((s, a) => s + a.total, 0);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  const isCategoryMode = resolvedCategory !== 'all';

  return (
    <div className="space-y-4 mt-4">

      {/* Category selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.areadrilldowntab.ver_origen_por_area_de')}</label>
          <Select
            value={resolvedCategory}
            onValueChange={(v) => { setSelectedCategory(v); setExpandedArea(null); }}
          >
            <SelectTrigger className="h-8 text-sm w-64">
              <SelectValue placeholder={t('subscriptions.areadrilldowntab.todas_las_categorias')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('subscriptions.areadrilldowntab.todas_las_categorias')}</SelectItem>
              {availableCategories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isCategoryMode && (
          <div className="mt-4">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectedCategory('all'); setExpandedArea(null); }}>
              {t('subscriptions.areadrilldowntab.ver_todo')}
            </Button>
          </div>
        )}
      </div>

      {/* === CATEGORY MODE: % breakdown by area === */}
      {isCategoryMode && (
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-4 border-b bg-muted/20">
            <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-0.5">{t('subscriptions.areadrilldowntab.origen_por_area_de_winclap')}</p>
            <p className="text-base font-semibold">{resolvedCategory}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total: <span className="font-medium text-foreground">{fmt(totalAll)}</span> — {areas.length} {t('subscriptions.areadrilldowntab.area')}{areas.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="divide-y">
            {areas.map((area, i) => {
              const pct = totalAll > 0 ? (area.total / totalAll) * 100 : 0;
              const isExpanded = expandedArea === area.area;
              const BAR_COLORS = ['#1CC9B9', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1', '#10b981'];
              const barColor = BAR_COLORS[i % BAR_COLORS.length];

              return (
                <div key={area.area}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors text-left"
                    onClick={() => setExpandedArea(isExpanded ? null : area.area)}
                  >
                    {/* Rank */}
                    <span className="text-xs text-muted-foreground w-4 shrink-0 font-medium">{i + 1}</span>

                    {/* Area name + bar */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{area.area}</span>
                        <span className="text-sm font-bold shrink-0" style={{ color: barColor }}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: barColor }}
                        />
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-sm font-semibold">{fmt(area.total)}</p>
                      <p className="text-xs text-muted-foreground">{area.vendors} vendor{area.vendors !== 1 ? 's' : ''}</p>
                    </div>

                    {/* Expand icon */}
                    {isExpanded
                      ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                    }
                  </button>

                  {/* Expanded: vendors in this area for the selected category */}
                  {isExpanded && (
                    <div className="bg-muted/20 border-t">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40">
                          <tr className="text-muted-foreground uppercase tracking-wide">
                            <th className="text-left py-2 px-6 font-medium">{t('subscriptions.areadrilldowntab.vendor_tool')}</th>
                            <th className="text-right py-2 px-3 font-medium">Monto</th>
                            <th className="text-right py-2 px-3 font-medium">{t('subscriptions.areadrilldowntab.del_area')}</th>
                            <th className="text-center py-2 px-3 font-medium">Flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {area.rows
                            .sort((a, b) => b.Total_Amount - a.Total_Amount)
                            .map((r) => {
                              const vendorPct = area.total > 0 ? (r.Total_Amount / area.total) * 100 : 0;
                              return (
                                <tr key={`${r.NetSuite_Departamentos}-${r.Normalized_Vendor}`} className="border-t hover:bg-muted/30">
                                  <td className="py-2 px-6 font-medium">{r.Normalized_Vendor}</td>
                                  <td className="py-2 px-3 text-right font-semibold">{fmt(r.Total_Amount)}</td>
                                  <td className="py-2 px-3 text-right text-muted-foreground">{vendorPct.toFixed(1)}%</td>
                                  <td className="py-2 px-3 text-center">
                                    <div className="flex gap-1 justify-center">
                                      {r.Is_AI_Tool && (
                                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">AI</Badge>
                                      )}
                                      {r.Is_AI_Native_Related && (
                                        <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">Native</Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      {area.area.toLowerCase().includes('indirect') && indirectRealAreas.length > 0 && (
                        <IndirectRealAreaPanel realAreas={indirectRealAreas} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* === DEFAULT MODE: stacked bar + full table === */}
      {!isCategoryMode && (
        <>
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-4">{t('subscriptions.area.chart_title')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, left: 100, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="area" tick={{ fontSize: 11 }} width={100} />
                <Tooltip
                  formatter={(v, name) => [fmt(Number(v)), name]}
                  labelFormatter={(label) => {
                    const item = chartData.find((d) => d.area === label);
                    return item?.fullArea ?? label;
                  }}
                />
                <Legend />
                <Bar dataKey="Non-AI" stackId="a" fill="#3b82f6" />
                <Bar dataKey="AI Tools" stackId="a" fill="#8b5cf6" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-medium">{t('subscriptions.area.dept_count', { n: areas.length })}</p>
              <div className="flex gap-2">
                <Button
                  variant={filterAI === undefined ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilterAI(undefined)}
                >{t('subscriptions.area.filter_all')}</Button>
                <Button
                  variant={filterAI === true ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilterAI(true)}
                >{t('subscriptions.area.filter_ai_only')}</Button>
              </div>
            </div>
            <div className="divide-y">
              {areas.map((area) => {
                const isExpanded = expandedArea === area.area;
                const aiPct = area.total > 0 ? (area.ai / area.total) * 100 : 0;
                const sharePct = totalAll > 0 ? (area.total / totalAll) * 100 : 0;
                const vendorRows = filterAI === true
                  ? area.rows.filter((r) => r.Is_AI_Tool)
                  : area.rows;

                if (filterAI === true && area.ai === 0) return null;

                return (
                  <div key={area.area}>
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                      onClick={() => setExpandedArea(isExpanded ? null : area.area)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{area.area}</span>
                          {area.ai > 0 && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 shrink-0">
                              {aiPct.toFixed(0)}% AI
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden w-full max-w-xs">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${sharePct}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0 space-y-0.5">
                        <p className="text-sm font-semibold">{fmt(area.total)}</p>
                        <p className="text-xs text-muted-foreground">{t('subscriptions.area.users_vendors', { users: area.users, vendors: area.vendors })}</p>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="bg-muted/20 border-t">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr className="text-muted-foreground uppercase tracking-wide">
                              <th className="text-left py-2 px-6 font-medium">{t('subscriptions.area.col_vendor')}</th>
                              <th className="text-left py-2 px-3 font-medium">{t('subscriptions.area.col_category')}</th>
                              <th className="text-right py-2 px-3 font-medium">{t('subscriptions.area.col_amount')}</th>
                              <th className="text-right py-2 px-3 font-medium">{t('subscriptions.area.col_txns')}</th>
                              <th className="text-right py-2 px-3 font-medium">{t('subscriptions.area.col_users')}</th>
                              <th className="text-center py-2 px-3 font-medium">{t('subscriptions.area.col_flags')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendorRows
                              .sort((a, b) => b.Total_Amount - a.Total_Amount)
                              .map((r) => (
                                <tr key={`${r.NetSuite_Departamentos}-${r.Normalized_Vendor}`} className="border-t hover:bg-muted/30">
                                  <td className="py-2 px-6 font-medium">{r.Normalized_Vendor}</td>
                                  <td className="py-2 px-3 text-muted-foreground">{r.Subscription_Category}</td>
                                  <td className="py-2 px-3 text-right font-semibold">{fmt(r.Total_Amount)}</td>
                                  <td className="py-2 px-3 text-right text-muted-foreground">{r.Transactions}</td>
                                  <td className="py-2 px-3 text-right text-muted-foreground">{r.Users}</td>
                                  <td className="py-2 px-3 text-center">
                                    <div className="flex gap-1 justify-center">
                                      {r.Is_AI_Tool && (
                                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">AI</Badge>
                                      )}
                                      {r.Is_AI_Native_Related && (
                                        <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">Native</Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                        {area.area.toLowerCase().includes('indirect') && indirectRealAreas.length > 0 && (
                          <IndirectRealAreaPanel realAreas={indirectRealAreas} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
